import React, { useState, useEffect, useCallback } from 'react';
import { IconoLapiz, IconoCheck, IconoCerrar, IconoRayo } from '../components/Iconos';

const API = process.env.REACT_APP_API_URL || '';
const token = () => localStorage.getItem('token');

// Colores ligados al tema (claro/oscuro) via variables CSS del shell de la app;
// solo los semánticos (accent/green/red/yellow) quedan fijos entre temas.
const C = {
  bg: 'var(--app-bg)',
  accent: '#00b8e3',
  card: 'var(--app-surface)',
  border: 'var(--app-border)',
  text: 'var(--app-text)',
  muted: 'var(--app-muted)',
  green: '#10b981',
  red: '#ef4444',
  yellow: '#f59e0b',
};

const css = {
  page: { background: C.bg, minHeight: '100vh', color: C.text, fontFamily: "'Space Grotesk', sans-serif", padding: '24px 16px 60px' },
  card: { background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 16 },
  input: { padding: '8px 12px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'var(--app-input)', color: C.text, fontSize: 14, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' },
  btn: (bg = C.accent) => ({ padding: '9px 18px', borderRadius: 8, border: 'none', background: bg, color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }),
  btnSm: (bg = C.accent) => ({ padding: '5px 12px', borderRadius: 6, border: 'none', background: bg, color: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }),
  label: { fontSize: 12, color: C.muted, marginBottom: 4, display: 'block' },
  th: { padding: '10px 12px', textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: C.muted, borderBottom: `1px solid ${C.border}` },
  td: { padding: '10px 12px', fontSize: 14, borderBottom: `1px solid ${C.border}` },
};

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const mesActual = () => { const h = new Date(); return `${MESES[h.getMonth()]} ${h.getFullYear()}`; };

function fmt(n) { return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n || 0); }

function badge(estado) {
  // Colores fijos (no var()): se les concatena un sufijo de opacidad hex.
  const colors = { PAGADO: C.green, PENDIENTE: C.yellow, INACTIVO: '#94a3b8' };
  const c = colors[estado] || '#94a3b8';
  return { display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: c + '22', color: c, border: `1px solid ${c}44` };
}

// ─── Modal alumno ─────────────────────────────────────────────────────────────

function ModalAlumno({ alumno, onClose, onSaved }) {
  const [form, setForm] = useState(alumno || { nombre: '', valor_mensualidad: '', telefono: '', notas: '', activo: 1 });
  const [err, setErr] = useState('');

  const guardar = async () => {
    if (!form.nombre || !form.valor_mensualidad) { setErr('Nombre y valor son requeridos'); return; }
    const metodo = alumno ? 'PUT' : 'POST';
    const url = alumno ? `${API}/api/academia/alumnos/${alumno.id}` : `${API}/api/academia/alumnos`;
    const r = await fetch(url, {
      method: metodo,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
      body: JSON.stringify({ ...form, valor_mensualidad: parseFloat(form.valor_mensualidad) }),
    });
    if (r.ok) { onSaved(); onClose(); }
    else { const d = await r.json(); setErr(d.error || 'Error'); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#00000088', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 28, width: '90%', maxWidth: 440 }}>
        <h3 style={{ margin: '0 0 20px', color: C.accent }}>{alumno ? 'Editar alumno' : 'Nuevo alumno'}</h3>
        {[
          { key: 'nombre', label: 'Nombre completo', type: 'text' },
          { key: 'valor_mensualidad', label: 'Valor mensualidad (COP)', type: 'number' },
          { key: 'telefono', label: 'Teléfono (opcional)', type: 'text' },
          { key: 'notas', label: 'Notas (opcional)', type: 'text' },
        ].map(({ key, label, type }) => (
          <div key={key} style={{ marginBottom: 14 }}>
            <label style={css.label}>{label}</label>
            <input style={css.input} type={type} value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} />
          </div>
        ))}
        {alumno && (
          <div style={{ marginBottom: 14 }}>
            <label style={css.label}>Estado</label>
            <select style={css.input} value={form.activo} onChange={e => setForm(f => ({ ...f, activo: parseInt(e.target.value) }))}>
              <option value={1}>Activo</option>
              <option value={0}>Inactivo</option>
            </select>
          </div>
        )}
        {err && <div style={{ color: C.red, fontSize: 13, marginBottom: 12 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn-ui btn-ui--md btn-ui--primary" onClick={guardar}>Guardar</button>
          <button className="btn-ui btn-ui--md btn-ui--outline" onClick={onClose}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal historial alumno ───────────────────────────────────────────────────

function ModalHistorial({ alumno, onClose }) {
  const [pagos, setPagos] = useState([]);
  useEffect(() => {
    fetch(`${API}/api/academia/alumnos/${alumno.id}/pagos`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.json()).then(d => setPagos(Array.isArray(d) ? d : [])).catch(() => {});
  }, [alumno.id]);

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#00000088', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 28, width: '90%', maxWidth: 500, maxHeight: '80vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, color: C.accent }}>Historial — {alumno.nombre}</h3>
          <button className="btn-ui btn-ui--outline btn-ui--icon" onClick={onClose} aria-label="Cerrar"><IconoCerrar /></button>
        </div>
        {pagos.length === 0
          ? <div style={{ color: C.muted, textAlign: 'center', padding: 24 }}>Sin registros</div>
          : pagos.map(p => (
            <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${C.border}` }}>
              <span style={{ fontSize: 14 }}>{p.mes}</span>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <span style={{ fontSize: 13 }}>{fmt(p.valor)}</span>
                <span style={badge(p.estado)}>{p.estado}</span>
                {p.fecha_pago && <span style={{ fontSize: 11, color: C.muted }}>{new Date(p.fecha_pago).toLocaleDateString('es-CO')}</span>}
              </div>
            </div>
          ))
        }
      </div>
    </div>
  );
}

// ─── Main Academia ────────────────────────────────────────────────────────────

export default function Academia() {
  const [alumnos, setAlumnos] = useState([]);
  const [mes, setMes] = useState(mesActual());
  const [pagos, setPagos] = useState([]);
  const [buscar, setBuscar] = useState('');
  const [modalAlumno, setModalAlumno] = useState(null);
  const [modalHistorial, setModalHistorial] = useState(null);
  const [cargando,      setCargando]      = useState(false);
  const [msgCarga,      setMsgCarga]      = useState('');
  const [editandoFecha, setEditandoFecha] = useState(null);
  const [fechaTemp,     setFechaTemp]     = useState('');
  const [anioSel] = useState(new Date().getFullYear());

  const mesesOpciones = [];
  for (let a = anioSel; a >= anioSel - 1; a--) {
    MESES.forEach(m => mesesOpciones.push(`${m} ${a}`));
  }

  const cargarAlumnos = useCallback(() => {
    fetch(`${API}/api/academia/alumnos`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.json()).then(d => setAlumnos(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  const cargarPagos = useCallback(() => {
    fetch(`${API}/api/academia/pagos?mes=${encodeURIComponent(mes)}`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.json()).then(d => setPagos(Array.isArray(d) ? d : [])).catch(() => {});
  }, [mes]);

  useEffect(() => { cargarAlumnos(); }, [cargarAlumnos]);
  useEffect(() => { cargarPagos(); }, [cargarPagos]);

  const cargarMes = async () => {
    setCargando(true); setMsgCarga('');
    const r = await fetch(`${API}/api/academia/cargar-mes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
      body: JSON.stringify({ mes }),
    });
    const d = await r.json();
    if (r.ok) { setMsgCarga(`✓ Se cargaron ${d.cargados} alumnos para ${mes}`); cargarPagos(); }
    else setMsgCarga(d.error || 'Error al cargar');
    setCargando(false);
  };

  const marcarPagado = async (pagoId) => {
    await fetch(`${API}/api/academia/pagos/${pagoId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
      body: JSON.stringify({ estado: 'PAGADO', fecha_pago: new Date().toISOString().slice(0, 10) }),
    });
    cargarPagos();
  };

  const marcarPendiente = async (pagoId) => {
    await fetch(`${API}/api/academia/pagos/${pagoId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
      body: JSON.stringify({ estado: 'PENDIENTE', fecha_pago: null }),
    });
    cargarPagos();
  };

  const cambiarFechaPago = async (pagoId, fecha) => {
    await fetch(`${API}/api/academia/pagos/${pagoId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
      body: JSON.stringify({ estado: 'PAGADO', fecha_pago: fecha }),
    });
    setEditandoFecha(null);
    cargarPagos();
  };

  // Cruzar alumnos activos con pagos del mes
  const alumnosActivos = alumnos.filter(a => a.activo === 1 || a.activo === '1');
  const pagoMap = Object.fromEntries(pagos.map(p => [p.alumno_id, p]));

  const filas = alumnosActivos
    .filter(a => a.nombre.toLowerCase().includes(buscar.toLowerCase()))
    .map(a => ({ alumno: a, pago: pagoMap[a.id] || null }));

  const totalEsperado = filas.reduce((s, { alumno }) => s + (parseFloat(alumno.valor_mensualidad) || 0), 0);
  const totalPagado = filas.reduce((s, { pago }) => s + (pago?.estado === 'PAGADO' ? parseFloat(pago.valor) || 0 : 0), 0);
  const totalPendiente = totalEsperado - totalPagado;
  const nPagados = filas.filter(({ pago }) => pago?.estado === 'PAGADO').length;
  const nPendientes = filas.filter(({ pago }) => !pago || pago.estado === 'PENDIENTE').length;

  return (
    <div style={css.page}>
      <div style={{ maxWidth: 960, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.accent }}>Academia</h1>
          <button className="btn-ui btn-ui--md btn-ui--primary" onClick={() => setModalAlumno({})}>+ Nuevo alumno</button>
        </div>

        {/* KPIs */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
          {[
            { label: 'Alumnos activos', val: alumnosActivos.length, unit: '' },
            { label: 'Pagados', val: nPagados, unit: '', color: C.green },
            { label: 'Pendientes', val: nPendientes, unit: '', color: C.yellow },
            { label: 'Recaudado', val: fmt(totalPagado), unit: '', color: C.green },
            { label: 'Pendiente', val: fmt(totalPendiente), unit: '', color: C.red },
          ].map(k => (
            <div key={k.label} style={{ ...css.card, marginBottom: 0, textAlign: 'center', padding: 16 }}>
              <div style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>{k.label}</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: k.color || C.text }}>{k.val}</div>
            </div>
          ))}
        </div>

        {/* Control de mes */}
        <div style={{ ...css.card, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label style={css.label}>Mes de cobro</label>
            <select style={css.input} value={mes} onChange={e => setMes(e.target.value)}>
              {mesesOpciones.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
            <button className="btn-ui btn-ui--md btn-ui--primary" onClick={cargarMes} disabled={cargando}>
              {cargando ? 'Cargando...' : <><IconoRayo /> Cargar mes</>}
            </button>
          </div>
          {msgCarga && <div style={{ width: '100%', fontSize: 13, color: msgCarga.startsWith('✓') ? C.green : C.red }}>{msgCarga}</div>}
        </div>

        {/* Buscador y tabla */}
        <div style={css.card}>
          <div style={{ marginBottom: 14 }}>
            <input style={css.input} placeholder="Buscar alumno..." value={buscar} onChange={e => setBuscar(e.target.value)} />
          </div>

          {/* Desktop */}
          <div className="tabla-desktop">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Alumno', 'Mensualidad', `Estado (${mes})`, 'Fecha pago', 'Acciones'].map(h => (
                    <th key={h} style={css.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filas.map(({ alumno, pago }) => (
                  <tr key={alumno.id} style={{ transition: 'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--app-surface-soft)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={css.td}>
                      <div style={{ fontWeight: 600 }}>{alumno.nombre}</div>
                      {alumno.telefono && <div style={{ fontSize: 11, color: C.muted }}>{alumno.telefono}</div>}
                    </td>
                    <td style={css.td}>{fmt(alumno.valor_mensualidad)}</td>
                    <td style={css.td}><span style={badge(pago?.estado || 'PENDIENTE')}>{pago?.estado || 'SIN CARGAR'}</span></td>
                    <td style={css.td}>
                      {editandoFecha === pago?.id ? (
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <input type="date" style={{ ...css.input, width: 130, fontSize: 12, padding: '4px 8px' }}
                            value={fechaTemp} onChange={e => setFechaTemp(e.target.value)} />
                          <button className="btn-ui btn-ui--primary btn-ui--icon" onClick={() => cambiarFechaPago(pago.id, fechaTemp)} aria-label="Guardar fecha"><IconoCheck /></button>
                          <button className="btn-ui btn-ui--outline btn-ui--icon" onClick={() => setEditandoFecha(null)} aria-label="Cancelar"><IconoCerrar /></button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <span>{pago?.fecha_pago ? new Date(pago.fecha_pago).toLocaleDateString('es-CO') : '—'}</span>
                          {pago?.estado === 'PAGADO' && (
                            <button className="btn-ui btn-ui--ghost btn-ui--icon" title="Cambiar fecha de pago" aria-label="Cambiar fecha de pago"
                              onClick={() => { setEditandoFecha(pago.id); setFechaTemp(pago.fecha_pago?.slice(0,10) || ''); }}><IconoLapiz /></button>
                          )}
                        </div>
                      )}
                    </td>
                    <td style={css.td}>
                      <div className="btn-ui-grid">
                        {pago && pago.estado === 'PENDIENTE' && (
                          <button className="btn-ui btn-ui--block btn-ui--primary" onClick={() => marcarPagado(pago.id)}>Pagado</button>
                        )}
                        {pago && pago.estado === 'PAGADO' && (
                          <button className="btn-ui btn-ui--block btn-ui--outline" onClick={() => marcarPendiente(pago.id)}>Revertir</button>
                        )}
                        {!pago && (
                          <button className="btn-ui btn-ui--block btn-ui--primary" onClick={async () => {
                            await fetch(`${API}/api/academia/pagos`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
                              body: JSON.stringify({ alumno_id: alumno.id, mes, valor: alumno.valor_mensualidad }),
                            });
                            cargarPagos();
                          }}>+ Agregar</button>
                        )}
                        <button className="btn-ui btn-ui--block btn-ui--outline" onClick={() => setModalHistorial(alumno)}>Historial</button>
                        <button className="btn-ui btn-ui--icon btn-ui--outline" title="Editar alumno" aria-label="Editar alumno" onClick={() => setModalAlumno(alumno)}><IconoLapiz /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filas.length === 0 && (
                  <tr><td colSpan={5} style={{ ...css.td, textAlign: 'center', color: C.muted, padding: 32 }}>Sin alumnos activos</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="cards-mobile">
            {filas.map(({ alumno, pago }) => (
              <div key={alumno.id} style={{ background: 'var(--app-surface-soft)', borderRadius: 10, padding: 14, marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>{alumno.nombre}</div>
                    <div style={{ fontSize: 12, color: C.muted }}>{fmt(alumno.valor_mensualidad)}/mes</div>
                  </div>
                  <span style={badge(pago?.estado || 'SIN CARGAR')}>{pago?.estado || 'SIN CARGAR'}</span>
                </div>
                <div className="btn-ui-grid btn-ui-grid--cards">
                  {pago && pago.estado === 'PENDIENTE' && <button className="btn-ui btn-ui--block btn-ui--primary" onClick={() => marcarPagado(pago.id)}>Pagado</button>}
                  {pago && pago.estado === 'PAGADO' && <button className="btn-ui btn-ui--block btn-ui--outline" onClick={() => marcarPendiente(pago.id)}>Revertir</button>}
                  {!pago && <button className="btn-ui btn-ui--block btn-ui--primary" onClick={async () => { await fetch(`${API}/api/academia/pagos`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` }, body: JSON.stringify({ alumno_id: alumno.id, mes, valor: alumno.valor_mensualidad }) }); cargarPagos(); }}>+ Agregar</button>}
                  <button className="btn-ui btn-ui--block btn-ui--outline" onClick={() => setModalHistorial(alumno)}>Historial</button>
                  <button className="btn-ui btn-ui--icon btn-ui--outline" title="Editar alumno" aria-label="Editar alumno" onClick={() => setModalAlumno(alumno)}><IconoLapiz /></button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Alumnos inactivos */}
        {alumnos.filter(a => a.activo === 0 || a.activo === '0').length > 0 && (
          <details style={{ ...css.card, cursor: 'pointer' }}>
            <summary style={{ fontSize: 13, color: C.muted, fontWeight: 600 }}>
              Alumnos inactivos ({alumnos.filter(a => a.activo === 0 || a.activo === '0').length})
            </summary>
            <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {alumnos.filter(a => a.activo === 0 || a.activo === '0').map(a => (
                <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: 'var(--app-surface-soft)', borderRadius: 8 }}>
                  <span style={{ fontSize: 14, color: C.muted }}>{a.nombre}</span>
                  <button className="btn-ui btn-ui--outline" onClick={() => setModalAlumno(a)}>Reactivar</button>
                </div>
              ))}
            </div>
          </details>
        )}
      </div>

      {modalAlumno !== null && (
        <ModalAlumno alumno={modalAlumno?.id ? modalAlumno : null} onClose={() => setModalAlumno(null)} onSaved={cargarAlumnos} />
      )}
      {modalHistorial && (
        <ModalHistorial alumno={modalHistorial} onClose={() => setModalHistorial(null)} />
      )}

      <style>{`
        .tabla-desktop { display: block; }
        .cards-mobile { display: none; }
        @media (max-width: 640px) {
          .tabla-desktop { display: none; }
          .cards-mobile { display: block; }
        }
      `}</style>
    </div>
  );
}
